/**
 * FERP PDF Suite Shell (DRR-079)
 *
 * 3-panel flex layout: left navigation, center viewer, right annotations.
 * Panels are collapsible via toolbar toggles.
 *
 * ┌──────────┬──────────────────────────────────────┬──────────┐
 * │          │         Enhanced PDFToolbar           │          │
 * │ Left     ├──────────────────────────────────────┤ Right    │
 * │ Panel    │           PDF Viewer                  │ Panel    │
 * │          │        (center, flex-grow)             │          │
 * └──────────┴──────────────────────────────────────┴──────────┘
 */

import { Box } from '@mantine/core';
import { usePdfSuite } from './PdfSuiteContext';
import { PdfLeftPanel } from './PdfLeftPanel';
import { PdfRightPanel } from './PdfRightPanel';
import type { ReactNode } from 'react';

interface PdfSuiteShellProps {
  /** The center content (PDFViewer + overlays) */
  children: ReactNode;
  /** Toolbar rendered above the 3-panel layout */
  toolbar: ReactNode;
  /** PDF document proxy for outline/metadata (from react-pdf onLoadSuccess) */
  pdfDocumentProxy?: unknown;
}

export function PdfSuiteShell({ children, toolbar, pdfDocumentProxy }: PdfSuiteShellProps) {
  const { state, isPro } = usePdfSuite();
  const showLeft = isPro && state.leftPanel !== null;
  const showRight = isPro && state.rightPanel !== null;

  return (
    <Box
      className="pdf-suite-shell"
      data-view-mode={state.isFullscreen ? 'presentation' : state.viewer.viewMode}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {/* Toolbar spans full width */}
      {toolbar}

      {/* 3-panel layout */}
      <Box style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left Panel (pro only) */}
        {showLeft && (
          <Box
            className="pdf-suite-left-panel"
            style={{
              width: 220,
              minWidth: 180,
              maxWidth: 300,
              borderRight: '1px solid var(--mantine-color-gray-3)',
              overflow: 'auto',
              flexShrink: 0,
            }}
          >
            <PdfLeftPanel pdfDocumentProxy={pdfDocumentProxy} />
          </Box>
        )}

        {/* Center Panel */}
        <Box
          className="pdf-suite-center-panel"
          style={{
            flex: 1,
            overflow: 'auto',
            position: 'relative',
            backgroundColor: 'var(--mantine-color-gray-1)',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {children}
        </Box>

        {/* Right Panel (pro only) */}
        {showRight && (
          <Box
            className="pdf-suite-right-panel"
            style={{
              width: 280,
              minWidth: 220,
              maxWidth: 380,
              borderLeft: '1px solid var(--mantine-color-gray-3)',
              overflow: 'auto',
              flexShrink: 0,
            }}
          >
            <PdfRightPanel />
          </Box>
        )}
      </Box>
    </Box>
  );
}
